/**
 * SearchBarCompactSimplified - Compact search bar for header navigation
 * 
 * Features:
 * - Inline +/- controls for guests and bedrooms
 * - Click-to-expand detailed options
 * - Shares global search state via SearchBarContext
 * - Compact design optimized for header placement
 * - Relative positioned dropdowns (no fixed positioning issues)
 * - Click-outside handlers for better UX
 * 
 * Used in: Header component when hero search is not visible
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CalendarModal } from './CalendarModal';
import { PortalDropdown } from './PortalDropdown';
import { ProIcon } from './ProIcon';
import { trackViewSearchResults } from '../lib/analytics';
import { useSearchBar } from '../contexts/SearchBarContext';

interface SearchBarCompactSimplifiedProps {
  className?: string;
  forceExpanded?: boolean;
}

export const SearchBarCompactSimplified: React.FC<SearchBarCompactSimplifiedProps> = ({ className = "", forceExpanded = false }) => {
  const navigate = useNavigate();
  const { searchParams, updateSearchParams } = useSearchBar();
  const [isMobile, setIsMobile] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'tight' | 'balanced' | 'full'>('balanced');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileOccupancy, setShowMobileOccupancy] = useState(false);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [showBedroomDropdown, setShowBedroomDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const guestButtonRef = useRef<HTMLButtonElement>(null);
  const bedroomButtonRef = useRef<HTMLButtonElement>(null);
  const isMobileFlowRef = useRef(false);
  
  const { dateRange, guests, bedrooms, flexibleDays } = searchParams;
  const [startDate, endDate] = dateRange;
  const totalGuests = guests.adults + guests.children + guests.infants;
  const useMinimalLayout = isMobile && !forceExpanded;

  const formatCompactDate = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });

  const formatFullDate = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  const numberOptions = (min: number, max: number) =>
    Array.from({ length: max - min + 1 }, (_, index) => min + index);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowGuestDropdown(false);
      setShowBedroomDropdown(false);
      return;
    }

    setShowMobileOccupancy(false);
  }, [isMobile]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resolveLayoutMode = (width: number) => {
      if (width >= 620) {
        setLayoutMode('full');
        return;
      }
      if (width >= 470) {
        setLayoutMode('balanced');
        return;
      }
      setLayoutMode('tight');
    };

    resolveLayoutMode(container.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? container.clientWidth;
      resolveLayoutMode(nextWidth);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const shouldShowBedrooms = !useMinimalLayout && layoutMode !== 'tight';
  const shouldShowTextLabels = layoutMode === 'full';
  const shouldShowDateText = layoutMode !== 'tight';

  const renderGuestLabel = () => {
    // When children > 0, show "X Adults · Y Children"
    if (guests.children > 0) {
      if (shouldShowTextLabels || layoutMode !== 'tight') {
        return `${guests.adults} Adults · ${guests.children} Children`;
      }
      return `${guests.adults}A · ${guests.children}C`;
    }
    
    // Show full label on full and balanced layouts, abbreviated only on tight/mobile
    if (shouldShowTextLabels || layoutMode !== 'tight') {
      return `${totalGuests} ${totalGuests === 1 ? 'Guest' : 'Guests'}`;
    }
    return `${totalGuests}G`;
  };

  const renderBedroomLabel = () => {
    if (bedrooms === 0) {
      return shouldShowTextLabels ? 'Any bedroom' : 'Any BR';
    }
    if (shouldShowTextLabels || layoutMode === 'balanced') {
      return `${bedrooms} ${bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}`;
    }
    return `${bedrooms}BR`;
  };

  const renderOccupancySummary = () => {
    const bedroomSummary = bedrooms === 0 ? 'Any BR' : `${bedrooms}BR`;
    return `${totalGuests}G · ${bedroomSummary}`;
  };

  const renderDate = () => {
    if (!startDate || !endDate) return shouldShowDateText ? 'Add dates' : '';

    const flexLabel = searchParams.isFlexible ? ` (±${searchParams.flexibleDays})` : '';

    if (isMobile) {
      return `${formatCompactDate(startDate)}-${formatCompactDate(endDate)}${flexLabel}`;
    }

    if (layoutMode === 'full') {
      return `${formatFullDate(startDate)} - ${formatFullDate(endDate)}${flexLabel}`;
    }

    // Tight mode: just show compact dates
    return `${formatCompactDate(startDate)}-${formatCompactDate(endDate)}${flexLabel}`;
  };

  const handleSearch = () => {
    // Infants are not included in search count
    const searchGuests = guests.adults + guests.children;
    const searchParamsString = new URLSearchParams({
      guests: searchGuests.toString(),
      bedrooms: bedrooms.toString(),
      startDate: startDate?.toISOString().split('T')[0] || '',
      endDate: endDate?.toISOString().split('T')[0] || '',
      flexible: searchParams.isFlexible.toString()
    });

    trackViewSearchResults('marbella rental search', {
      guests: searchGuests,
      adults: guests.adults,
      children: guests.children,
      infants: guests.infants,
      bedrooms,
      check_in: startDate?.toISOString().split('T')[0],
      check_out: endDate?.toISOString().split('T')[0],
      flexible: searchParams.isFlexible
    });

    navigate(`/destinations?${searchParamsString.toString()}`);
  };

  return (
    <>
      <div ref={containerRef} className={`flex w-full min-w-0 items-center justify-center gap-1 flex-nowrap ${className}`}>
        {useMinimalLayout ? (
          <>
            {/* Mobile Single Pill Design */}
            <button
              onClick={() => setShowMobileSearch(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur-sm rounded-full border border-slate-200 shadow-sm min-h-[40px]"
              aria-label="Search dates, guests and bedrooms"
              title="Tap to search"
            >
              <ProIcon icon="calendar-range" family="solid" className="w-4 h-4 text-brand-ocean flex-shrink-0" />
              <span className="text-sm font-semibold text-slate-700 truncate">
                {startDate && endDate 
                  ? `${formatCompactDate(startDate)}-${formatCompactDate(endDate)}${searchParams.isFlexible ? ` (±${searchParams.flexibleDays})` : ''} · ${bedrooms === 0 ? 'Any' : `${bedrooms}br`}`
                  : 'Add dates'}
              </span>
              <ProIcon icon="magnifying-glass" family="utility" className="w-4 h-4 text-brand-sun flex-shrink-0 ml-1" />
            </button>
          </>
        ) : (
          <>
        {/* Dates */}
        <button 
          onClick={() => setShowCalendar(true)}
          className="ui-control-chip flex min-w-0 flex-1 gap-1.5 px-2 py-2"
        >
          <ProIcon icon="calendar-range" family="solid" className="w-4 h-4 text-slate-600 flex-shrink-0" />
          {shouldShowDateText && (
            <span className="min-w-0 ui-chip-text truncate">
              {renderDate()}
            </span>
          )}
        </button>

        {/* Guests */}
        <div className="relative flex-shrink-0">
          <>
            <button 
              ref={guestButtonRef}
              onClick={() => {
                setShowGuestDropdown(!showGuestDropdown);
                setShowBedroomDropdown(false);
              }}
              className="ui-control-chip gap-1.5 px-2 py-2"
              aria-label={`${totalGuests} ${totalGuests === 1 ? 'Guest' : 'Guests'}`}
              title={`${totalGuests} ${totalGuests === 1 ? 'Guest' : 'Guests'}`}
            >
              <ProIcon icon="family-dress" family="solid" className="w-4 h-4 text-slate-600 flex-shrink-0" />
              <span className="ui-chip-text max-w-[84px] truncate">
                {renderGuestLabel()}
              </span>
            </button>

            <PortalDropdown
              isOpen={showGuestDropdown}
              onClose={() => setShowGuestDropdown(false)}
              targetRef={guestButtonRef}
              className="ui-card-elevated rounded-xl p-4 min-w-[200px]"
            >
              <div className="space-y-3">
                <label className="block text-sm font-bold text-slate-700">Adults</label>
                <select
                  value={guests.adults}
                  onChange={(e) => updateSearchParams({ guests: { ...guests, adults: Number(e.target.value) } })}
                  className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-900 outline-none focus:border-brand-sun"
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Adult' : 'Adults'}</option>
                  ))}
                </select>
                
                <label className="block text-sm font-bold text-slate-700">Children</label>
                <select
                  value={guests.children}
                  onChange={(e) => updateSearchParams({ guests: { ...guests, children: Number(e.target.value) } })}
                  className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-900 outline-none focus:border-brand-sun"
                >
                  {Array.from({ length: 11 }, (_, i) => i).map((num) => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Child' : 'Children'}</option>
                  ))}
                </select>
                
                <label className="block text-sm font-bold text-slate-700">Infants</label>
                <select
                  value={guests.infants}
                  onChange={(e) => updateSearchParams({ guests: { ...guests, infants: Number(e.target.value) } })}
                  className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-900 outline-none focus:border-brand-sun"
                >
                  {Array.from({ length: 5 }, (_, i) => i).map((num) => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Infant' : 'Infants'}</option>
                  ))}
                </select>
              </div>
            </PortalDropdown>
          </>
        </div>

        {/* Bedrooms */}
        {shouldShowBedrooms && (
        <div className="relative flex-shrink-0">
          <>
            <button 
              ref={bedroomButtonRef}
              onClick={() => {
                setShowBedroomDropdown(!showBedroomDropdown);
                setShowGuestDropdown(false);
              }}
              className="ui-control-chip gap-1.5 px-2 py-2"
              aria-label={bedrooms === 0 ? 'Any bedroom' : `${bedrooms} ${bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}`}
              title={bedrooms === 0 ? 'Any bedroom' : `${bedrooms} ${bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}`}
            >
              <ProIcon icon="bed" family="solid" className="w-4 h-4 text-slate-600 flex-shrink-0" />
              <span className="ui-chip-text max-w-[84px] truncate">
                {renderBedroomLabel()}
              </span>
            </button>

            <PortalDropdown
              isOpen={showBedroomDropdown}
              onClose={() => setShowBedroomDropdown(false)}
              targetRef={bedroomButtonRef}
              className="ui-card-elevated rounded-xl p-4 min-w-[180px]"
            >
              <div className="space-y-3">
                <label className="block text-sm font-bold text-slate-700">Bedrooms</label>
                <select
                  value={bedrooms}
                  onChange={(e) => updateSearchParams({ bedrooms: Number(e.target.value) })}
                  className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-900 outline-none focus:border-brand-sun"
                >
                  {Array.from({ length: 11 }, (_, i) => i).map((num) => (
                    <option key={num} value={num}>
                      {num === 0 ? 'Any bedroom' : `${num} ${num === 1 ? 'bedroom' : 'bedrooms'}`}
                    </option>
                  ))}
                </select>
              </div>
            </PortalDropdown>
          </>
        </div>
        )}

        {/* Search Button */}
        <button 
          onClick={handleSearch}
          className="ui-btn-primary flex-shrink-0 h-9 w-9 rounded-xl p-0 shadow-md"
          aria-label="Search available rentals"
          title="Search available rentals"
        >
          <ProIcon icon="magnifying-glass" family="utility" className="w-4 h-4 text-white" />
        </button>
          </>
        )}
      </div>

      {showMobileSearch && createPortal(
        <div className="fixed inset-0 z-[9998] md:hidden bg-white flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
            <h2 className="text-lg font-bold text-slate-900">Find your rental</h2>
            <button
              onClick={() => setShowMobileSearch(false)}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
              aria-label="Close search"
            >
              <ProIcon icon="xmark" family="light" className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Dates Section */}
            <div className="space-y-3">
              <label className="block text-sm font-bold text-slate-700">When do you want to stay?</label>
              <button
                onClick={() => {
                  isMobileFlowRef.current = true;
                  setShowMobileSearch(false);
                  setTimeout(() => setShowCalendar(true), 50);
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <ProIcon icon="calendar-range" family="solid" className="w-5 h-5 text-brand-ocean" />
                <div className="flex-1">
                  {startDate && endDate ? (
                    <>
                      <div className="font-bold text-slate-900">{formatCompactDate(startDate)} - {formatCompactDate(endDate)}</div>
                      <div className="text-xs text-slate-500">
                        {Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} nights
                        {searchParams.isFlexible && ` · ±${searchParams.flexibleDays} days flexible`}
                      </div>
                    </>
                  ) : (
                    <span className="text-slate-500">Select dates</span>
                  )}
                </div>
                <span className="text-brand-ocean text-sm font-medium">Change</span>
              </button>
            </div>

            {/* Guests & Bedrooms Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Guests */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-700">Guests</label>
                <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-white">
                  <ProIcon icon="family-dress" family="solid" className="w-4 h-4 text-brand-ocean" />
                  <select
                    value={totalGuests}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      updateSearchParams({ guests: { adults: value, children: 0, infants: 0 } });
                    }}
                    className="flex-1 bg-transparent text-sm font-semibold text-slate-900 outline-none"
                  >
                    {numberOptions(1, 16).map((value) => (
                      <option key={`mobile-guests-${value}`} value={value}>{value} {value === 1 ? 'guest' : 'guests'}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bedrooms */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-700">Bedrooms</label>
                <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-white">
                  <ProIcon icon="bed" family="solid" className="w-4 h-4 text-brand-ocean" />
                  <select
                    value={bedrooms}
                    onChange={(e) => updateSearchParams({ bedrooms: Number(e.target.value) })}
                    className="flex-1 bg-transparent text-sm font-semibold text-slate-900 outline-none"
                  >
                    {numberOptions(0, 10).map((value) => (
                      <option key={`mobile-bedrooms-${value}`} value={value}>
                        {value === 0 ? 'Any' : `${value} ${value === 1 ? 'br' : 'brs'}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with Search Button */}
          <div className="p-4 border-t border-slate-100 bg-white safe-area-pb">
            <button
              onClick={() => {
                handleSearch();
                setShowMobileSearch(false);
              }}
              className="w-full py-4 bg-brand-sun hover:bg-brand-sun-dark text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 transition-colors"
            >
              <ProIcon icon="magnifying-glass" family="utility" className="w-5 h-5" />
              <span>SEARCH RENTALS</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Occupancy Sheet - kept for compatibility */}
      {showMobileOccupancy && createPortal(
        <div className="fixed inset-0 z-[9998] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setShowMobileOccupancy(false)}
            aria-label="Close guests and bedrooms"
          />
          <div className="absolute inset-x-0 bottom-0 ui-radius-panel-top border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-black text-slate-900">Guests & bedrooms</h3>
                <p className="mt-1 text-sm text-slate-500">Keep the header search fast and simple on mobile.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-2">
                  <span className="block ui-button-text text-slate-500">Guests</span>
                  <select
                    value={totalGuests}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      updateSearchParams({ guests: { adults: value, children: 0, infants: 0 } });
                    }}
                    className="ui-select-mobile"
                    aria-label="Guests"
                  >
                    {numberOptions(1, 16).map((value) => (
                      <option key={`total-guests-${value}`} value={value}>{value} {value === 1 ? 'guest' : 'guests'}</option>
                    ))}
                  </select>
                </label>

                <label className="space-y-2">
                  <span className="block ui-button-text text-slate-500">Bedrooms</span>
                  <select
                    value={bedrooms}
                    onChange={(e) => updateSearchParams({ bedrooms: Number(e.target.value) })}
                    className="ui-select-mobile"
                    aria-label="Bedrooms"
                  >
                    {numberOptions(0, 10).map((value) => (
                      <option key={`bedrooms-mobile-${value}`} value={value}>{value === 0 ? 'Any bedroom' : `${value} ${value === 1 ? 'bedroom' : 'bedrooms'}`}</option>
                    ))}
                  </select>
                </label>
              </div>

              <button
                type="button"
                className="ui-btn-ocean w-full px-4 py-3 text-sm"
                onClick={() => setShowMobileOccupancy(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Calendar Modal */}
      <CalendarModal
        isOpen={showCalendar}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setShowCalendar(false)}
        onDatesChange={(dates, flexible = false, flexDays = 0) => {
          updateSearchParams({ dateRange: dates, isFlexible: flexible, flexibleDays: flexDays });
          setShowCalendar(false);
          // Auto-navigate only when NOT in mobile search flow
          // Mobile flow: reopen search window to allow guest/bedroom selection
          if (isMobileFlowRef.current) {
            setShowMobileSearch(true);
          } else {
            handleSearch();
          }
          // Reset the ref for next time
          isMobileFlowRef.current = false;
        }}
        initialFlexibleDays={flexibleDays}
      />
    </>
  );
};
